import * as ewe from "@eyeo/webext-ad-filtering-solution";
import browser from "webextension-polyfill";
import { EXPERIMENT_FLAG, GET_DIALOG_MESSAGE_TYPE, DIALOG_ACTION_MESSAGE_TYPE, DISMISS_DIALOG_MESSAGE_TYPE, STEP_PREF_KEY, TRIAL_START_DATE_PREF_KEY, TRIAL_DURATION_DAYS, MS_PER_DAY, } from "../shared/constants.js";
import { resolveNextTouchPoint, LAST_TOUCH_POINT_STEP } from "./touchpoints.js";
const serverLogger = new ewe.telemetry.ServerLogger("reverse_trial");
async function toDataUrl(url) {
    const response = await fetch(url);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return `data:${blob.type};base64,${btoa(binary)}`;
}
async function ensureTrialIsNotInProgress(params) {
    var _a, _b;
    const currentStep = (_a = params.prefs.get(STEP_PREF_KEY)) !== null && _a !== void 0 ? _a : 0;
    const trialStartDate = (_b = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _b !== void 0 ? _b : 0;
    if (trialStartDate && currentStep < LAST_TOUCH_POINT_STEP) {
        await params.prefs.set(STEP_PREF_KEY, LAST_TOUCH_POINT_STEP);
    }
}
async function requestDialogHandler(params) {
    var _a, _b, _c;
    if (await params.hasPremium()) {
        await ensureTrialIsNotInProgress(params);
        return null;
    }
    const lastShown = (_a = params.prefs.get(STEP_PREF_KEY)) !== null && _a !== void 0 ? _a : 0;
    let trialStartDate = (_b = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _b !== void 0 ? _b : 0;
    const flagEnabled = Boolean(await ewe.experiments.getFlag(EXPERIMENT_FLAG));
    if (!flagEnabled && !trialStartDate) {
        return null;
    }
    if (!trialStartDate) {
        trialStartDate = Date.now();
        await params.prefs.set(TRIAL_START_DATE_PREF_KEY, trialStartDate);
    }
    const elapsedDays = Math.floor((Date.now() - trialStartDate) / MS_PER_DAY);
    const config = resolveNextTouchPoint(lastShown, elapsedDays);
    if (!config) {
        return null;
    }
    const daysLeft = Math.max(0, TRIAL_DURATION_DAYS - elapsedDays);
    const titleSubs = config.step === 2 ? [String(daysLeft)] : undefined;
    const logoUrl = await toDataUrl(params.logoUrl);
    serverLogger.info("dialog_shown", {
        touchPointStep: config.step,
        installDate: (_c = params.prefs.get("installDate")) !== null && _c !== void 0 ? _c : 0,
        reverseTrialStartDate: trialStartDate,
    });
    const urlParams = new URLSearchParams();
    urlParams.set("trialStarted", String(trialStartDate));
    const isOptedOut = await params.isOptedOut();
    if (!isOptedOut) {
        urlParams.set("s", "reverse_trial_opd");
        urlParams.set("opdStep", String(config.step));
        urlParams.set("rcid", await ewe.telemetry.getRotatingClientId());
        urlParams.set("av", params.addonVersion);
        urlParams.set("er", String(await ewe.remoteConfig.getRevisionId()));
        urlParams.set("ev", await ewe.experiments.getEncodedExperiments());
        urlParams.set("ca", await ewe.cohorts.getEncodedCohorts());
    }
    const premiumUrl = `${params.premiumUrl}?${urlParams.toString()}`;
    let target = "";
    if (config.hasTarget) {
        target = premiumUrl;
    }
    let learnMore;
    if (config.learnMore) {
        learnMore = {
            text: browser.i18n.getMessage(config.learnMore),
            url: premiumUrl,
        };
    }
    return {
        title: browser.i18n.getMessage(config.titleKey, titleSubs),
        body: config.bodyKeys.map((entry) => ({
            text: browser.i18n.getMessage(entry.key),
            style: entry.style,
        })),
        button: browser.i18n.getMessage(config.buttonKey),
        target,
        logoUrl,
        touchPointStep: config.step,
        learnMore,
    };
}
async function dialogActionHandler(message, params) {
    var _a, _b, _c;
    const currentStep = (_a = params.prefs.get(STEP_PREF_KEY)) !== null && _a !== void 0 ? _a : 0;
    if (currentStep < message.touchPointStep) {
        await params.prefs.set(STEP_PREF_KEY, message.touchPointStep);
    }
    serverLogger.info("dialog_action", {
        action: message.action,
        touchPointStep: message.touchPointStep,
        installDate: (_b = params.prefs.get("installDate")) !== null && _b !== void 0 ? _b : 0,
        reverseTrialStartDate: (_c = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _c !== void 0 ? _c : 0,
    });
    await dismissOpenDialogs();
}
async function dismissOpenDialogs() {
    const tabs = await browser.tabs.query({});
    await Promise.all(tabs.map(async (tab) => {
        if (tab.id !== undefined) {
            try {
                await browser.tabs.sendMessage(tab.id, {
                    type: DISMISS_DIALOG_MESSAGE_TYPE,
                });
            }
            catch (_e) {
            }
        }
    }));
}
export function start(params) {
    params.port.on(GET_DIALOG_MESSAGE_TYPE, async () => await requestDialogHandler(params));
    params.port.on(DIALOG_ACTION_MESSAGE_TYPE, async (message) => {
        await dialogActionHandler(message, params);
    });
    const trustedTypes = [GET_DIALOG_MESSAGE_TYPE, DIALOG_ACTION_MESSAGE_TYPE];
    params.addTrustedMessageTypes("https://youtube.com", trustedTypes);
    params.addTrustedMessageTypes("https://www.youtube.com", trustedTypes);
    void params.hasPremium().then(async (premium) => {
        if (premium) {
            await ensureTrialIsNotInProgress(params);
        }
    });
}
